import React, { useState, useEffect } from 'react';
import { Card, Spinner, Badge } from 'react-bootstrap';
import { useNavigate } from 'react-router-dom';
import { generateSlug } from '../utils/slugUtils';
import { useAuth } from '../context/AuthContext';
import { viewerLocation, nearbyUrl, nearbyGroups, groupHeading } from '../utils/nearby';

function NearbyRestaurants({ restaurantId }) {
  const [groups, setGroups] = useState([]);
  const [radius, setRadius] = useState(5);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const navigate = useNavigate();
  const { token } = useAuth();

  const apiBaseUrl = process.env.REACT_APP_API_BASE_URL || 'http://localhost:8000';

  useEffect(() => {
    fetchNearbyRestaurants();
    // Refetch when auth changes so a logged-in owner's own private adds (#141)
    // appear alongside public results.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [restaurantId, token]);

  const fetchNearbyRestaurants = async () => {
    setLoading(true);
    setError(null);
    try {
      // Send the token when logged in so the API can include the viewer's own
      // private restaurants in nearby results (#141); anonymous → public only.
      const headers = token ? { Authorization: `Bearer ${token}` } : {};
      // One request: the backend picks the anchor (#369) and falls back to the
      // nearest places when nothing is within the radius (#368). The old 5/25/100
      // widening hit the server's 10 mi cap and errored (#367).
      const response = await fetch(
        nearbyUrl(apiBaseUrl, restaurantId, await viewerLocation()),
        { headers }
      );

      if (!response.ok) {
        throw new Error('Failed to fetch nearby restaurants');
      }

      const data = await response.json();
      setGroups(nearbyGroups(data));
      if (data.radius_miles) setRadius(data.radius_miles);
    } catch (err) {
      console.error('Error fetching nearby restaurants:', err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleRestaurantClick = (restaurant) => {
    const slug = generateSlug(restaurant.name);
    navigate(`/restaurant/${restaurant.id}/${slug}`);
    // Scroll to top when navigating to new restaurant
    window.scrollTo(0, 0);
  };

  if (loading) {
    return (
      <div className="text-center py-3">
        <Spinner animation="border" size="sm" /> Loading nearby restaurants...
      </div>
    );
  }

  if (error) {
    return (
      <div className="text-center text-muted py-3">
        <small>Unable to load nearby restaurants</small>
      </div>
    );
  }

  if (groups.length === 0) {
    return (
      <div className="text-center text-muted py-3">
        <small>No other restaurants on dyner.z yet</small>
      </div>
    );
  }

  const grouped = groups.length > 1;

  const renderGroup = (group) => {
    const nearby = group.nearby_restaurants;
    const heading = groupHeading(group, { grouped, radius });
    return (
    <div key={group.anchor ? group.anchor.location_id : 'all'} className={grouped ? 'mb-3' : ''}>
      {heading && (
        <div className="text-center mb-2">
          <small className="text-muted" style={{ fontSize: '0.8rem', fontWeight: '500' }}>{heading}</small>
        </div>
      )}
      <div style={{ textAlign: 'center' }}>
        <div
          style={{
            display: 'inline-flex',
            overflowX: 'auto',
            gap: '12px',
            paddingBottom: '8px',
            scrollBehavior: 'smooth',
            WebkitOverflowScrolling: 'touch',
            maxWidth: '100%',
            textAlign: 'left'
          }}
        >
            {nearby.map((restaurant) => (
            <div
              key={`${restaurant.id}-${restaurant.location_id}`}
              style={{
                flex: '0 0 auto',
                width: '145px',
                cursor: 'pointer'
              }}
              onClick={() => handleRestaurantClick(restaurant)}
            >
              <Card className="h-100 shadow-sm hover-card">
                {restaurant.primary_image ? (
                  <Card.Img
                    variant="top"
                    src={restaurant.primary_image}
                    alt={restaurant.name}
                    style={{
                      height: '90px',
                      objectFit: 'cover'
                    }}
                  />
                ) : (
                  <div
                    style={{
                      height: '90px',
                      backgroundColor: '#f0f0f0',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center'
                    }}
                  >
                    <span style={{ fontSize: '1.5rem' }}>🍽️</span>
                  </div>
                )}
                <Card.Body className="p-2">
                  <Card.Title
                    style={{
                      fontSize: '0.8rem',
                      fontWeight: '600',
                      marginBottom: '0.25rem',
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                      whiteSpace: 'nowrap'
                    }}
                  >
                    {restaurant.name}
                  </Card.Title>
                  <div className="small text-success fw-bold mb-1" style={{ fontSize: '0.7rem' }}>
                    {restaurant.distance} mi away
                    {restaurant.location_name && ` (${restaurant.location_name})`}
                  </div>
                  {restaurant.cuisines && restaurant.cuisines.length > 0 && (
                    <div
                      className="small text-muted"
                      style={{
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        whiteSpace: 'nowrap',
                        fontSize: '0.65rem'
                      }}
                    >
                      {restaurant.cuisines.join(' • ')}
                    </div>
                  )}
                  {!!restaurant.is_closed && (
                    <Badge bg="danger" style={{ fontSize: '0.55rem', marginTop: '0.25rem' }}>
                      CLOSED
                    </Badge>
                  )}
                  {restaurant.total_locations > 1 && (
                    <Badge bg="info" style={{ fontSize: '0.55rem', marginTop: '0.25rem', marginLeft: restaurant.is_closed ? '0.25rem' : '0' }}>
                      {restaurant.total_locations} locations
                    </Badge>
                  )}
                </Card.Body>
              </Card>
            </div>
            ))}
          </div>
        </div>

      {nearby.length >= 6 && (
        <div className="text-center mt-2">
          <small className="text-muted" style={{ fontSize: '0.75rem' }}>
            ← Scroll to see all {nearby.length} nearby restaurants →
          </small>
        </div>
      )}
    </div>
    );
  };

  return <div>{groups.map(renderGroup)}</div>;
}

export default NearbyRestaurants;
